// Word-level diff between two strings based on LCS over whitespace-separated
// tokens. Returns an array of { type: 'same' | 'added' | 'removed', text }.
//
// "added" means the token appears in the second string only (introduced by
// whatever produced the second text — e.g. the LLM). "removed" means the token
// appears in the first string only.
//
// O(m*n) space + time. Fine for transcript segments (typically <100 tokens).

export function wordDiff(a, b) {
  if (a === b) return [{ type: 'same', text: a }];

  const ta = tokenize(a);
  const tb = tokenize(b);

  // LCS dp[i][j] = length of LCS of ta[..i] and tb[..j]
  const m = ta.length;
  const n = tb.length;
  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = ta[i - 1] === tb[j - 1]
        ? dp[i - 1][j - 1] + 1
        : Math.max(dp[i - 1][j], dp[i][j - 1]);
    }
  }

  const result = [];
  let i = m, j = n;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && ta[i - 1] === tb[j - 1]) {
      result.unshift({ type: 'same', text: ta[i - 1] });
      i--; j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      result.unshift({ type: 'added', text: tb[j - 1] });
      j--;
    } else {
      result.unshift({ type: 'removed', text: ta[i - 1] });
      i--;
    }
  }

  return coalesce(result);
}

// Tokenize preserving whitespace as separate tokens so we can reassemble
// a readable output without collapsing spacing.
function tokenize(s) {
  if (!s) return [];
  return s.match(/\s+|[^\s]+/g) || [];
}

// Merge consecutive tokens of the same type to minimise JSX nodes.
function coalesce(arr) {
  const out = [];
  for (const t of arr) {
    const last = out[out.length - 1];
    if (last && last.type === t.type) last.text += t.text;
    else out.push({ ...t });
  }
  return out;
}
